import React, {
  useReducer,
  useState,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from 'react';
import {
  Box,
  InputBase,
  makeStyles,
  Button,
  ButtonBase,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import _ from 'lodash';
import moment from 'moment';
import { PhotoIcon, CheckListIcon, FileIcon, TagIcon, AddIcon, DeleteIcon } from '../../../../components/Icon';
import { AutoCompleteInput, DragDropUpload } from '../../../../components';
import { i18n } from '../../../../language';
import { ehandoverAPI } from '../../../../apis';
import lengthVerification, { remainingLength } from '../../../../utils/lengthVerification';
import ehandoverIds from '../../../../ids/Ehandover';
import { getArrayRandomKey, revokePreviewUrl, isNotEmpty, getUserInfo, initValue, onKeyUpEnterOrSpace } from '../../../../utils';
import IndividualUserDialog from '../IndividualUserDialog';
import ShowSelectedFiles from '../ShowSelectedFiles';
import { ACCEPT_FILE_TYPES } from '../../../../utils/hooks/useUploadFile';
import { WarpSwitch } from '../../../../components/WarpSwitch';
import UseTemplateDialog from '../UseTemplateDialog';
import CustomPrompt from '../../../../components/CustomPrompt';
import { getValBasedOnChecklistDisplay } from '../../../../utils/eHandoverUtil';
import Postday from './postday';
import Tag from './tag';
import Checklist from './checklist';
import useTaskFileUpload from './useTaskFileUpload';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '2.438rem 0 2.215rem 0',
    borderBottom: '0.0625rem solid #dddddd',
    display: 'flex',
  },
  from: {
    borderShadows: '0 0.418rem 1rem rgba(0,0,0,0.04)',
    width: '100%',
    backgroundColor: theme.palette.font.white,
    borderRadius: '0.9375rem',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 0 0.875rem 0 ',
    margin: '0 1.875rem -0.0625rem',
  },
  headBox: {
    margin: '0 0 0.857rem 0',
    padding: '1rem 1.875rem 0',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headResetBtn: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  foot: {
    padding: '1rem 0 2.813rem',
    margin: '0 1.875rem -0.0625rem',
    borderTop: '0.0625rem solid #ddd',
    display: 'flex',
    flexWrap: 'wrap',
  },
  body: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.25rem 0 0',
    margin: '0 1.875rem 0.5rem',
    // borderTop: '0.0625rem solid #ddd',
    alignItems: 'flex-start',
    gridGap: '1rem',
    flexWrap: 'wrap',
  },
  tagsBox: { display: 'flex', gridGap: '0.5rem', margin: '0 1.875rem 2.25rem', flexWrap: 'wrap' },
  tags: {
    display: 'flex',
    // flexWrap: 'wrap',
    alignItems: 'center',
    gridGap: '0.5rem',
    flex: 1,
  },
  tagIcon: {
    fontSize: '1.375rem',
    color: theme.palette.font.sub,
  },
  input: {
    fontFamily: 'NotoSans-Medium',
    border: '0',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    width: '100%',
    fontWeight: '500',
    letterSpacing: '0',
    color: theme.palette.font.main,
    height: '100%',
  },
  grid: {
    width: '100%',
    height: '3.813rem',
    borderTop: '0.0625rem solid #e4e7eb',
    overflow: 'hidden',
    borderRadius: '0 0 0.9375rem 0.9375rem',
    display: 'flex',
    '& $paper:last-child': {
      borderRight: 'none',
    },
  },
  paper: {
    textAlign: 'center',
    height: '100%',
    borderRight: '0.0625rem #e4e7eb solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  text: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.25rem',
    lineHeight: '1.875rem',
    color: theme.palette.font.main,
  },
  icon: {
    fontSize: '1.5rem',
    marginRight: '0.75rem',
    color: '#5f9df6',
  },
  btnBox: {
    padding: '0.8rem 1.875rem',
    '& .MuiButton-root': {
      color: theme.palette.font.sub,
      fontFamily: 'NotoSans-Medium',
      fontWeight: '500',
    },
    '& .MuiButton-root.MuiButton-textPrimary': {
      color: theme.palette.primary.main,
    },
  },
  checklistBtn: {
    textTransform: 'inherit',
  },
  inputfile: {
    width: '0.1px',
    height: '0.1px',
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    zIndex: -1,
  },
  label: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  file: {
    width: '1rem',
    height: '1.25rem',
    color: theme.palette.primary.main,
  },
  inputTip: {
    fontFamily: 'NotoSans-Medium',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    margin: '0 1.875rem -0.0625rem',
    borderBottom: '0.0625rem solid #ddd',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  groupBox: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'NotoSans-Medium',
    color: '#2E4469',
    gridGap: '0.5rem',
  },
  inputBox: {
    padding: '0.642rem 0.857rem',
    borderRadius: '0.714rem',
    border: '1px solid #E4E7EB',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: '0.5rem',
    cursor: 'pointer',
    width: '25rem',
  },
  groupName: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#2E4469',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  editBtn: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#538FFF',
  },
  notifyBox: {
    margin: '0 1.875rem 1.25rem',
    paddingTop: '1.3rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gridGap: '1.714rem',
    borderTop: '0.0625rem solid #ddd',
  },
  checkBox: {
    marginLeft: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.5rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1.142rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  acknowledgeBox: {
    marginLeft: '0rem',
    '& .MuiFormControlLabel-label': {
      marginLeft: '0.571rem',
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      fontWeight: '500',
      fontSize: '1.142rem',
    },
  },
  requireAckTips: {
    marginTop: '0.357rem',
    fontFamily: 'NotoSans-Medium',
    color: '#F44336',
  },
}));

const WarnAlert = ({ text, open, onClose }) => {
  return (
    <Snackbar open={open} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={() => onClose()}>
      <Alert severity="warning">{text}</Alert>
    </Snackbar>
  );
};

function checkListReducer(state, action) {
  switch (action.type) {
    case 'add':
      return [...state, action.item];
    case 'remove':
      return [...state.slice(0, action.index), ...state.slice(action.index + 1)];
    case 'reset':
      return [];
    case 'change':
      state[action.index].text = action.text;
      return [...state];
    case 'checklistReset':
      const length = state.length;
      if (length > 0) {
        return [{ id: state[length - 1].id + 1, text: '' }];
      }
      return [{ id: 1, text: '' }];
    case 'applyClone':
      return action.items;
    default:
      throw new Error();
  }
}

function tagReducer(state, action) {
  switch (action.type) {
    case 'add':
      return [...state, action.item];
    case 'remove':
      return [...state.slice(0, action.index), ...state.slice(action.index + 1)];
    case 'reset':
      return [];
    case 'change':
      state[action.index].text = action.text;
      return [...state];
    case 'applyClone':
      return action.items;
    default:
      throw new Error();
  }
}

const CreateNote = forwardRef(function (
  { updateData, openResetConfirmationDialog, individualUsers, getIndividualUsers, templateList, tagList, isNotToSendAsDefault = false },
  ref
) {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const [tags, tagsDispatch] = useReducer(tagReducer, []);
  const [checklist, checklistDispatch] = useReducer(checkListReducer, [{ id: 1, text: '' }]);
  const [checklistopen, setChecklistopen] = useState(false);
  const [tagId, setTagId] = useState(null);
  const inputRef = useRef(null);
  const [titleEmptyAlert, setTitleEmptyAlert] = useState(false);
  const [acknowledgeWarnOpen, setAcknowledgeWarnOpen] = useState(false);
  const [roomTypeList, setRoomTypeList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const {
    imgList,
    setImgList,
    fileList,
    setFileList,
    uploadTaskId,
    uploadTaskIdRef,
    setUploadDraftTaskId,
    alertOpen,
    setAlertOpen,
    warnFile,
    setWarnFile,
    fileTypeWarn,
    attachmentLimitWarnOpen,
    setAttachmentLimitWarnOpen,
    attachmentUploadingWarnOpen,
    setAttachmentUploadingWarnOpen,
    isUploadingAttachment,
    handleSelectFile,
    handleDragDropUpload,
    handleDeletePhoto,
    handleDeleteFile,
  } = useTaskFileUpload();
  // notify config
  const [notifyBaseOnSendTo, setNotifyBaseOnSendTo] = useState(true);
  const [notifyConfig, setNotifyConfig] = useState({
    ITEM_COMPLETE: false,
    CHECKLIST_COMPLETE: false,
  });
  const needNotifyOptions = [
    {
      label: i18n.t('EHANDOVER_ITEM_COMPLETE'),
      type: 'ITEM_COMPLETE',
    },
    {
      label: i18n.t('EHANDOVER_CHECKLIST_COMPLETE'),
      type: 'CHECKLIST_COMPLETE',
      isChecked: false,
    },
  ];
  const [showNotifyListDialog, setShowNotifyListDialog] = useState(false);
  const [notifyList, setNotifyList] = useState({
    roomTypeList: [],
    staffList: [],
  });
  // acknowLedge
  const [requiredAcknowLedge, setRequiredAcknowLedge] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  
  // record use template statistics
  const [currentTemplateId, setCurrentTemplateId] = useState(null);

  // 1、是复制已经发送的task到create note，会弹窗是否覆盖掉已经输入的内容
  // 2、create note是否输入了东西，已经输入过就离开页面会弹是否保存成draft弹窗
  useEffect(() => {
    const defaultRoomType = _.find(individualUsers?.roomTypeList, (item) => item?.value === 1);

    const isRoomTypeEmpty = (roomTypes) => {
      if (roomTypes.length === 0) return false;
      if (roomTypes.length > 1) return true;
      // If there's exactly 1 item, check if it's the default one
      if (defaultRoomType && roomTypes[0]?.value === defaultRoomType.value) return false;
      return true;
    };

    const hasNotSaveTask =
      isNotEmpty(value) ||
      isNotEmpty((checklist ?? []).filter((item) => item.text !== '')) ||
      isNotEmpty(imgList) ||
      isNotEmpty(fileList) ||
      isNotEmpty(tags) ||
      isRoomTypeEmpty(roomTypeList) ||
      isNotEmpty(staffList) ||
      isRoomTypeEmpty(notifyList?.roomTypeList) ||
      isNotEmpty(notifyList?.staffList);
    setIsDirty(hasNotSaveTask);
  }, [value, tags, checklist, imgList, fileList, roomTypeList, staffList, notifyList, individualUsers]);

  useEffect(() => {
    const defaultRoomType = _.find(individualUsers?.roomTypeList, (item) => item?.value === 1);
    setRoomTypeList(defaultRoomType && !isNotToSendAsDefault ? [defaultRoomType] : []);
    // baseOnSendTo默认是true,并且notify的默认值跟sendTo一样
    setNotifyList((prev) => ({
      ...prev,
      roomTypeList: defaultRoomType && !isNotToSendAsDefault ? [defaultRoomType] : [],
    }));
  }, [individualUsers]);

  // 用户上传的附件大于3个的时候禁用require acknowledge
  const disabledAcknowledgement = useMemo(() => {
    const totalAttachments = (imgList?.length ?? 0) + (fileList?.length ?? 0);
    return totalAttachments > 3;
  }, [imgList, fileList]);

  useImperativeHandle(
    ref,
    () => {
      return {
        applyClone: (data) => {
          tagsDispatch({
            type: 'applyClone',
            items: (data?.tags ?? []).map((t) => ({ tag: t.tagDesc, tagId: t.tagId })),
          });
          setValue(data?.itemData?.description ?? '');

          let importChecklist = (data?.checks ?? []).filter((item) => item.PARENT_ID === 0);
          if (importChecklist.length > 0) {
            setChecklistopen(true);
            checklistDispatch({
              type: 'applyClone',
              items: importChecklist.map((item, index) => ({ id: index + 1, text: item.TITLE })),
            });
          } else {
            setChecklistopen(false);
            checklistDispatch({ type: 'checklistReset' });
          }

          setTagId(null);

          const sendTo = data?.sendTo ?? [];
          setRoomTypeList(sendTo.map((item) => ({ text: item.desc, value: item.roomType })));
          inputRef.current.focus();
        },
        isDirty: () => {
          return isDirty;
        },
      };
    },
    [isDirty]
  );
  const [showIndividualUserDialog, setShowIndividualUserDialog] = useState(false);
  const roomTypeRef = useRef(null);

  const individualDisplayName = useMemo(() => {
    const roomTypeDisplayName = _.map(roomTypeList, (item) => item?.name);
    const staffDisplayName = _.map(staffList, (item) => item?.staffName);

    if (roomTypeDisplayName.length !== 0 || staffDisplayName.length !== 0) {
      return [...roomTypeDisplayName, ...staffDisplayName].join(',');
    } else {
      return '';
    }
  }, [roomTypeList, staffList]);

  const notifyDisplayName = useMemo(() => {
    const roomTypeDisplayName = _.map(notifyList.roomTypeList, (item) => item?.name);
    const staffDisplayName = _.map(notifyList.staffList, (item) => item?.staffName);

    if (roomTypeDisplayName.length !== 0 || staffDisplayName.length !== 0) {
      return [...roomTypeDisplayName, ...staffDisplayName].join(',');
    } else {
      return '';
    }
  }, [notifyList]);

  // create task API
  const postCreateTaskAPI = async (formInfo) => {
    try {
      await ehandoverAPI.postCreateTask(formInfo);
      updateData();
      handleReset();
    } catch (error) {
      console.log('-----Create Task error----->', error);
    }
  };

  const updateTaskAPI = async (formInfo, updateType = 'share') => {
    try {
      const apiMap = {
        share: ehandoverAPI.updateShareTask,
        schedule: ehandoverAPI.updateScheduleTask,
        draft: ehandoverAPI.updateMyDraftTask,
      };
      const targetApi = apiMap[updateType] ?? ehandoverAPI.updateShareTask;

      await targetApi(formInfo);
      updateData();
      handleReset();
    } catch (error) {
      console.log('-----Update Task error----->', error);
    }
  };

  const getTaskFiles = useCallback(() => {
    return [...imgList, ...fileList];
  }, [imgList, fileList]);

  const getCommonSubmitData = useCallback(
    ({ selectedDate = null, isDraft = false, customRecurrence = {} }) => {
      const checkListCompleteIsChecked = notifyConfig?.CHECKLIST_COMPLETE;
      const itemCompleteIsChecked = notifyConfig?.ITEM_COMPLETE;
      const newChecklist = _.map(
        checklist.filter((item) => item.text !== ''),
        (item, index) => ({
          isComplete: 'N',
          sortIndex: index,
          title: item.text,
        })
      );
      const checklistCorpList = _.map(notifyList?.staffList, (item) => ({
        corpId: item?.corpId,
        staffName: item?.staffName,
      }));
      const checklistRoomTypes = _.map(notifyList?.roomTypeList, (item) => item?.value);
      const postDay = selectedDate
        ? moment(selectedDate).format('yyyy-MM-DD HH:mm:00')
        : moment().format('yyyy-MM-DD HH:mm:ss');

      return {
        title: value,
        tags: _.map(tags, (item) => item.tagId),
        description: value,
        postDay,
        roomType: _.map(roomTypeList, (item) => item?.value),
        corpList: _.map(staffList, (item) => ({ corpId: item?.corpId, staffName: item?.staffName })),
        checklistComplete: getValBasedOnChecklistDisplay(checklistopen, 'N', checkListCompleteIsChecked ? 'Y' : 'N'),
        itemComplete: getValBasedOnChecklistDisplay(checklistopen, 'N', itemCompleteIsChecked ? 'Y' : 'N'),
        checklistConfig: {
          checklistCorpList: getValBasedOnChecklistDisplay(checklistopen, [], checklistCorpList),
          checklistRoomTypes: getValBasedOnChecklistDisplay(checklistopen, [], checklistRoomTypes),
          isBasedSendTo: getValBasedOnChecklistDisplay(checklistopen, 'Y', notifyBaseOnSendTo ? 'Y' : 'N'),
        },
        draft: isDraft,
        mark: requiredAcknowLedge ? 'N' : null,
        ...customRecurrence,
        daysOfWeek: customRecurrence?.daysOfWeek?.join(','),
        newChecklist,
      };
    },
    [
      notifyConfig,
      checklist,
      notifyList,
      value,
      tags,
      roomTypeList,
      staffList,
      checklistopen,
      notifyBaseOnSendTo,
      requiredAcknowLedge,
    ]
  );

  const handleSubmitTask = useCallback(
    async ({ selectedDate = null, isDraft = false, customRecurrence = {} }) => {
      if (isUploadingAttachment) {
        setAttachmentUploadingWarnOpen(true);
        return;
      }
      const commonData = getCommonSubmitData({ selectedDate, isDraft, customRecurrence });
      const taskfiles = getTaskFiles();
      const currentUploadTaskId = uploadTaskIdRef.current || uploadTaskId;
      if (currentUploadTaskId) {
        const updateParams = {
          addCheckLists: getValBasedOnChecklistDisplay(
            checklistopen,
            [],
            _.map(commonData.newChecklist, (item) => ({
              ...item,
              parentId: 0,
            }))
          ),
          addTaskFiles: [],
          checkLists: [],
          delCheckItemIds: [],
          fields: {
            description: commonData.description,
            postDay: commonData.postDay,
            tags: commonData.tags,
            title: commonData.title,
          },
          filesIds: _.compact(_.map(taskfiles, (item) => item?.id)),
          roomType: commonData.roomType,
          corpList: commonData.corpList,
          taskId: currentUploadTaskId,
          checklistComplete: commonData.checklistComplete,
          itemComplete: commonData.itemComplete,
          checklistConfig: commonData.checklistConfig,
          mark: commonData.mark,
          draft: commonData.draft,
        };

        if (isDraft) {
          await updateTaskAPI(
            {
              ...updateParams,
              ...customRecurrence,
              daysOfWeek: customRecurrence?.daysOfWeek?.join(','),
            },
            'draft'
          );
          return;
        }

        if (selectedDate) {
          await updateTaskAPI(
            {
              ...updateParams,
              ...customRecurrence,
              daysOfWeek: customRecurrence?.daysOfWeek?.join(','),
              isJustThisOne: false,
            },
            'schedule'
          );
          return;
        }

        await updateTaskAPI(updateParams, 'share');
        return;
      }

      await postCreateTaskAPI({
        title: commonData.title,
        tags: commonData.tags,
        description: commonData.description,
        postDay: commonData?.postDay ?? moment().format('yyyy-MM-DD HH:mm:ss'),
        checkLists: getValBasedOnChecklistDisplay(checklistopen, [], commonData.newChecklist),
        taskFiles: _.map(taskfiles, (item) => ({
          content: item.base64[0],
          name: item.name,
        })),
        roomType: commonData.roomType,
        corpList: commonData.corpList,
        checklistComplete: commonData.checklistComplete,
        itemComplete: commonData.itemComplete,
        checklistConfig: commonData.checklistConfig,
        draft: commonData.draft,
        mark: commonData.mark,
        ...customRecurrence,
        daysOfWeek: customRecurrence?.daysOfWeek?.join(','),
        templateId: currentTemplateId
      });
    },
    [checklistopen, getCommonSubmitData, getTaskFiles, isUploadingAttachment, uploadTaskId, uploadTaskIdRef]
  );

  const handleSaveDraft = async () => {
    if (isUploadingAttachment) {
      setAttachmentUploadingWarnOpen(true);
      throw new Error('Attachments are still uploading');
    }
    if (!isNotEmpty(value)) {
      handleInputFocus();
      setTitleEmptyAlert(true);
      // Throw an error to indicate save failed so that CustomPrompt knows to stop navigation
      throw new Error('Value is required');
    }
    await handleSubmitTask({ isDraft: true });
  };

  const handleAddClick = (tagId) => {
    if (!tagId || !_.isNumber(tagId)) return;

    const currentTag = _.find(tagList, (item) => item?.value === tagId);
    tagsDispatch({ type: 'add', item: { tag: currentTag?.text, tagId: currentTag?.value } });
  };

  const handleReset = useCallback(() => {
    const defaultRoomType = _.find(individualUsers?.roomTypeList, (item) => item?.value === 1);
    revokePreviewUrl(imgList);
    setUploadDraftTaskId(null);
    tagsDispatch({ type: 'reset' });
    setValue('');
    checklistDispatch({ type: 'checklistReset' });
    setImgList([]);
    setFileList([]);
    setTagId(null);
    setNotifyList({ roomTypeList: defaultRoomType && !isNotToSendAsDefault ? [defaultRoomType] : [], staffList: [] });
    setRoomTypeList(defaultRoomType && !isNotToSendAsDefault ? [defaultRoomType] : []);
    setStaffList([]);
    setNotifyBaseOnSendTo(true);
    setNotifyConfig({
      ITEM_COMPLETE: false,
      CHECKLIST_COMPLETE: false,
    });
    setRequiredAcknowLedge(false);
    roomTypeRef?.current?.handleReset?.();
    setCurrentTemplateId(null);
  }, [individualUsers, imgList, setUploadDraftTaskId]);

  const handleInputFocus = () => {
    inputRef.current.focus();
  };

  const handleConfirmIndividual = ({ selectedRoom, selectedStaff, isNotify }) => {
    if (isNotify) {
      setNotifyList({ roomTypeList: selectedRoom, staffList: selectedStaff });
      setShowNotifyListDialog(false);
    } else {
      setRoomTypeList(selectedRoom);
      setStaffList(selectedStaff);
      if (notifyBaseOnSendTo) {
        setNotifyList({ roomTypeList: selectedRoom, staffList: selectedStaff });
      }
      setShowIndividualUserDialog(false);
    }
  };

  const onClickResetInput = useCallback(() => {
    if (isUploadingAttachment) {
      setAttachmentUploadingWarnOpen(true);
      return;
    }

    openResetConfirmationDialog(async () => {
      const currentUploadTaskId = uploadTaskIdRef.current || uploadTaskId;
      if (currentUploadTaskId) {
        await ehandoverAPI.deleteTaskDraft({
          taskId: currentUploadTaskId,
          deletedBy: initValue(getUserInfo()?.staffName, getUserInfo()?.corpId),
        });
      }

      handleReset();
    });
  }, [
    handleReset,
    isUploadingAttachment,
    openResetConfirmationDialog,
    setAttachmentUploadingWarnOpen,
    uploadTaskId,
    uploadTaskIdRef,
  ]);

  const handleOnClick = async (type) => {
    if (individualUsers === null) {
      await getIndividualUsers();
    }
    type === 'notify' ? setShowNotifyListDialog(true) : setShowIndividualUserDialog(true);
  };

  const updateNotifyOptionStatus = (key, isChecked) => {
    setNotifyConfig((prev) => ({
      ...prev,
      [key]: isChecked,
    }));
  };

  const handleApplyTaskTemplate = (templateData) => {
    handleReset();
    setCurrentTemplateId(templateData?.templateId)
    setValue(templateData?.description);
    setRequiredAcknowLedge(templateData?.requiredAcknowLedge);
    setNotifyBaseOnSendTo(templateData?.notifyBaseOnSendTo);
    setRoomTypeList(templateData?.sendToList?.roomTypeList);
    setStaffList(templateData?.sendToList?.staffList);
    setNotifyList({
      roomTypeList: templateData?.notifyList?.roomTypeList,
      staffList: templateData?.notifyList?.staffList,
    });
    setNotifyConfig({
      ITEM_COMPLETE: templateData?.itemComplete,
      CHECKLIST_COMPLETE: templateData?.checklistComplete,
    });
    templateData?.tags.forEach((item) => handleAddClick(item?.tagId));
    if (Array.isArray(templateData?.checkLists) && templateData?.checkLists?.length > 0) {
      setChecklistopen(true);
      checklistDispatch({
        type: 'applyClone',
        items: templateData?.checkLists.map((item, index) => ({ id: index + 1, text: item.title })),
      });
    }
  };

  const handleAcknowledgeAndAttachments = (nextRequiredAcknowLedge) => {
    const totalAttachments = (imgList?.length ?? 0) + (fileList?.length ?? 0);
    let acknowledgeStatus = nextRequiredAcknowLedge;
    if (nextRequiredAcknowLedge && totalAttachments > 3) {
      acknowledgeStatus = false;
      setAcknowledgeWarnOpen(true);
    }
    setRequiredAcknowLedge(acknowledgeStatus);
  };

  useEffect(() => {
    if (requiredAcknowLedge) {
      handleAcknowledgeAndAttachments(true);
    }
  }, [imgList, fileList]);

  return (
    <Box className={classes.root}>
      <input
        type="file"
        name="photo"
        id="photo"
        accept=".jpg,.png,.jpeg"
        capture="camera"
        onChange={(event) => {
          (async () => {
            await handleSelectFile(event?.target?.files);
          })();
        }}
        multiple
        className={classes.inputfile}
        onClick={(event) => {
          event.target.value = '';
        }}
      />
      <input
        type="file"
        name="file"
        id="file"
        accept={ACCEPT_FILE_TYPES.join(',')}
        onChange={(event) => {
          (async () => {
            await handleSelectFile(event?.target?.files);
          })();
        }}
        multiple
        className={classes.inputfile}
        onClick={(event) => {
          event.target.value = '';
        }}
      />
      <WarnAlert
        open={acknowledgeWarnOpen}
        onClose={() => setAcknowledgeWarnOpen(false)}
        text={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGEMENT_LIMIT_WARN')}
      />
      <WarnAlert
        open={attachmentLimitWarnOpen}
        onClose={() => setAttachmentLimitWarnOpen(false)}
        text="A maximum of 10 attachments can be uploaded."
      />
      <WarnAlert
        open={attachmentUploadingWarnOpen}
        onClose={() => setAttachmentUploadingWarnOpen(false)}
        text="Please wait until all attachments finish uploading."
      />
      <WarnAlert
        open={titleEmptyAlert}
        onClose={() => setTitleEmptyAlert(false)}
        text={'Please type in your message before clicking on "Save As Draft".'}
      />
      <WarnAlert
        text={
          fileTypeWarn.length > 0
            ? i18n.t('EHANDOVER_FILE_TYPE_WARN', {
                text: ACCEPT_FILE_TYPES.map((v) => {
                  return `"${v.replace('.', '')}"`;
                }).join(','),
              })
            : i18n.t('EHANDOVER_FILE_WARN', { value: warnFile.toString() })
        }
        open={alertOpen}
        onClose={() => {
          setAlertOpen(false);
          setWarnFile([]);
        }}
      />
      <DragDropUpload onUpload={handleDragDropUpload}>
        <div className={classes.from}>
          <Box className={classes.headBox} display="flex" flexDirection="row" alignItems="space-between">
            <Box>
              <FormControlLabel
                control={
                  <WarpSwitch
                    checked={requiredAcknowLedge}
                    onChange={(event) => handleAcknowledgeAndAttachments(event.target.checked)}
                    color="primary"
                    disabled={disabledAcknowledgement}
                  />
                }
                label={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGEMENT')}
                className={classes.acknowledgeBox}
              />
              {disabledAcknowledgement && (
                <Box className={classes.requireAckTips}>{i18n.t('EHANDOVER_REQUIRE_ACK_TIPS')}</Box>
              )}
            </Box>
            <Box display="flex" flexDirection="row" gridGap="1.428rem">
              <div id={`${ehandoverIds.inputReset}`} className={classes.headResetBtn} onClick={onClickResetInput} onKeyUp={(e) => onKeyUpEnterOrSpace(e, () => onClickResetInput())}>
                {i18n.t('EHANDOVER_RESET')}
              </div>
              <div
                id={`${ehandoverIds.inputReset}`}
                className={classes.headResetBtn}
                onClick={() => setShowTemplateDialog(true)}
                onKeyUp={(e) => onKeyUpEnterOrSpace(e, () => setShowTemplateDialog(true))}
              >
                {i18n.t('EHANDOVER_USE_TEMPLATE')}
              </div>
            </Box>
          </Box>
          <div className={classes.head}>
            <InputBase
              id={`${ehandoverIds.createNote}1`}
              className={classes.input}
              placeholder={i18n.t('EHANDOVER_CREATENOTE_INPUT')}
              multiline
              variant="outlined"
              inputRef={inputRef}
              value={value}
              onChange={(event) => {
                if (lengthVerification(event.target.value, 3001)) {
                  return;
                }
                setValue(event.target.value);
              }}
            />
          </div>
          <div className={classes.inputTip}>
            {i18n.t('EHANDOVER_CREATENOTE_INPUT_REMAINING', { value: remainingLength(value, 3000) })}
          </div>
          {checklistopen && (
            <div style={{ paddingTop: '1.813rem' }}>
              {checklist.map((item, index) => {
                return (
                  <Checklist
                    text={item.text}
                    checklistDispatch={checklistDispatch}
                    checklist={checklist}
                    index={index}
                    key={item.id}
                    id={ehandoverIds.checklist + index}
                    deleteId={ehandoverIds.checklist_delete + index}
                  />
                );
              })}
              <Box display="flex" justifyContent="space-between" className={classes.btnBox}>
                <Button
                  id={`${ehandoverIds.createNote}2`}
                  className={classes.checklistBtn}
                  color="primary"
                  startIcon={<AddIcon style={{ fontSize: '1.063rem' }} />}
                  onClick={() => {
                    if (checklist.length === 0) {
                      checklistDispatch({ type: 'add', item: { id: 1, text: '' } });
                      return;
                    }
                    const last = checklist.length - 1;
                    checklistDispatch({ type: 'add', item: { id: checklist[last].id + 1, text: '' } });
                  }}
                >
                  {i18n.t('EHANDOVER_CREATENOTE_CHECKLIST_ADD')}
                </Button>
                <Button
                  id={`${ehandoverIds.createNote}3`}
                  className={classes.checklistBtn}
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    checklistDispatch({ type: 'checklistReset' });
                  }}
                >
                  {i18n.t('EHANDOVER_CREATENOTE_CHECKLIST_DELETE')}
                </Button>
              </Box>
              <Box className={classes.notifyBox}>
                <Box display="flex" flexDirection="row" alignItems="center" gridGap="0.857rem">
                  <Box fontSize="1.142rem" fontWeight="500" fontFamily="NotoSans-Medium">
                    {i18n.t('EHANDOVER_NOTIFY')}:
                  </Box>
                  <Box className={classes.groupBox}>
                    <Box
                      className={classes.inputBox}
                      style={{ width: '23rem' }}
                      onClick={() => handleOnClick('notify')}
                    >
                      <Box
                        className={classes.groupName}
                        style={{
                          color: notifyDisplayName ? '#2E4469' : 'rgb(125, 134, 169)',
                          fontSize: notifyDisplayName ? '1.142rem' : '1rem',
                        }}
                      >
                        {notifyDisplayName || i18n.t('EHANDOVER_SELECT')}
                      </Box>
                      <Box className={classes.editBtn}>{i18n.t('EHANDOVER_EDIT')}</Box>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="row" alignItems="center" gridGap="0.857rem">
                  <Box fontSize="1.142rem" fontWeight="500" fontFamily="NotoSans-Medium">
                    {i18n.t('EHANDOVER_WHEN')}
                  </Box>
                  {_.map(needNotifyOptions, (item, index) => (
                    <FormControlLabel
                      key={getArrayRandomKey(index, 'ehandover-notify')}
                      control={<Checkbox />}
                      label={item.label}
                      className={classes.checkBox}
                      checked={notifyConfig[item?.type]}
                      onChange={(event) => updateNotifyOptionStatus(item.type, event?.target?.checked)}
                    />
                  ))}
                </Box>
              </Box>
            </div>
          )}

          <Box>
            <div className={classes.body}>
              <Box display="flex" alignItems="flex-start" gridGap="1rem" flexWrap="wrap">
                <div className={classes.tags}>
                  <TagIcon className={classes.tagIcon} />
                  <AutoCompleteInput
                    id={`${ehandoverIds.createNote}4`}
                    width="15.6rem"
                    height="2.5rem"
                    placeholder={i18n.t('EHANDOVER_CREATENOTE_TAG')}
                    itemList={tagList}
                    value={tagId}
                    onChange={(id) => {
                      setTagId(id);
                      if (!_.some(tags, (item) => item?.tagId === id)) {
                        handleAddClick(id);
                      }
                    }}
                  />
                </div>
                <Box className={classes.groupBox}>
                  <Box fontSize="1.142rem" fontWeight={500} width="max-content">
                    {i18n.t('EHANDOVER_SEND_TO')}
                  </Box>
                  <Box className={classes.inputBox} onClick={() => handleOnClick('sendTo')}>
                    <Box
                      className={classes.groupName}
                      style={{
                        color: individualDisplayName ? '#2E4469' : 'rgb(125, 134, 169)',
                        fontSize: individualDisplayName ? '1.142rem' : '1rem',
                      }}
                    >
                      {individualDisplayName || i18n.t('NOTIFICATION_FORWARD')}
                    </Box>
                    <Box className={classes.editBtn}>{i18n.t('EHANDOVER_EDIT')}</Box>
                  </Box>
                </Box>
              </Box>
              <Box display="flex" gridGap="1.875rem" alignItems="flex-start">
                <Postday
                  value={value}
                  handleInputFocus={handleInputFocus}
                  WarnAlert={WarnAlert}
                  handleSubmitTask={handleSubmitTask}
                  disabled={isUploadingAttachment}
                />
              </Box>
            </div>
            <Box className={classes.tagsBox}>
              {tags.map((tag, index) => (
                <Tag
                  id={ehandoverIds.tag_delete + index}
                  tag={tag}
                  key={`${ehandoverIds.tag_delete}_${index}`}
                  onRemove={() => tagsDispatch({ type: 'remove', index })}
                />
              ))}
            </Box>
          </Box>
          <ShowSelectedFiles
            photo={imgList}
            files={fileList}
            setPhoto={setImgList}
            setFiles={setFileList}
            handleDeletePhoto={handleDeletePhoto}
            handleDeleteFile={handleDeleteFile}
          />
          <Box className={classes.grid}>
            <Box
              id={`${ehandoverIds.createNote}5`}
              className={classes.paper}
              flexGrow={1}
              onClick={() => {
                if (checklistopen) {
                  setChecklistopen(false);
                  return;
                }
                setChecklistopen(true);
              }}
            >
              <ButtonBase>
                <CheckListIcon className={classes.icon} />
              </ButtonBase>
              <span className={classes.text}>{i18n.t('EHANDOVER_CREATENOTE_CHECKLIST')}</span>
            </Box>
            <Box className={classes.paper} flexGrow={1}>
              <label id={`${ehandoverIds.createNote}6`} htmlFor="photo" className={classes.label}>
                <PhotoIcon className={classes.icon} />
                <span className={classes.text}>{i18n.t('EHANDOVER_CREATENOTE_PHOTO')}</span>
              </label>
            </Box>
            <Box className={classes.paper} flexGrow={1}>
              <label id={`${ehandoverIds.createNote}7`} htmlFor="file" className={classes.label}>
                <FileIcon className={classes.icon} />
                <span className={classes.text}>{i18n.t('EHANDOVER_CREATENOTE_FILE')}</span>
              </label>
            </Box>
          </Box>
        </div>
      </DragDropUpload>
      {showIndividualUserDialog && (
        <IndividualUserDialog
          open={showIndividualUserDialog}
          closePop={() => setShowIndividualUserDialog(false)}
          roomTypeList={roomTypeList}
          staffList={staffList}
          individualUsers={individualUsers}
          handleConfirm={handleConfirmIndividual}
          notifyParam={null}
        />
      )}
      {showNotifyListDialog && (
        <IndividualUserDialog
          open={showNotifyListDialog}
          closePop={() => setShowNotifyListDialog(false)}
          roomTypeList={notifyList?.roomTypeList}
          staffList={notifyList?.staffList}
          individualUsers={individualUsers}
          handleConfirm={handleConfirmIndividual}
          notifyParam={{
            notifyBaseOnSendTo,
            setNotifyBaseOnSendTo,
            sendToRoomTypeList: roomTypeList,
            sendToStaffList: staffList,
          }}
        />
      )}
      {showTemplateDialog && (
        <UseTemplateDialog
          open={showTemplateDialog}
          closePop={() => setShowTemplateDialog(false)}
          templateList={templateList}
          handleApplyTaskTemplate={handleApplyTaskTemplate}
          isDirty={isDirty}
          individualUsers={individualUsers}
        />
      )}

      <CustomPrompt
        when={isDirty}
        confirmAction={handleSaveDraft}
        confirmText={i18n.t('EHANDOVER_SAVE_AS_DRAFT')}
        cancelText={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
        titleText={i18n.t('EHANDOVER_LEAVE_PAGE')}
        contentText={i18n.t('EHANDOVER_LEAVE_PAGE_CONTENT')}
      />
    </Box>
  );
});

export default CreateNote;

export { CreateNote, WarnAlert };
